import { z } from 'zod';
import { idempotencyKeySchema } from './common.schemas';

export const MAX_SYNC_POSITIONS_PER_BATCH = 100;

/** Where a position came from. `gps` is the vehicle tracker's (gitaumoses4/chukua#84); the phone only ever sends `phone`. */
export const POSITION_SOURCES = ['phone', 'gps'] as const;
export type PositionSource = (typeof POSITION_SOURCES)[number];

/**
 * One fix from the driver's phone. Not `.strict()`, like the rest of the sync contract: a newer app may add fields.
 * `key` is the phone's own id for the fix, echoed in the result; the server's identity for a position is trip, source and
 * device time, so a fix sent twice under any key is a duplicate.
 */
export const syncPositionSchema = z.object({
  key: idempotencyKeySchema,
  tripId: z.uuid(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().min(0).max(100_000).optional(),
  speedMps: z.number().min(0).max(200).optional(),
  headingDeg: z.number().min(0).lt(360).optional(),
  /** The phone's clock, with its offset. The server stamps its own time separately. */
  deviceTime: z.iso.datetime({ offset: true }),
});
export type SyncPositionInput = z.infer<typeof syncPositionSchema>;

/** `POST /sync/positions`. */
export const syncPositionsRequestSchema = z.object({ positions: z.array(syncPositionSchema).min(1).max(MAX_SYNC_POSITIONS_PER_BATCH) });
export type SyncPositionsRequest = z.infer<typeof syncPositionsRequestSchema>;

/** Permanent reasons a position is refused; the app drops it rather than retrying. */
export const SYNC_POSITION_REJECTION_REASONS = ['trip_not_found', 'time_out_of_range', 'trip_reassigned'] as const;
export type SyncPositionRejectionReason = (typeof SYNC_POSITION_REJECTION_REASONS)[number];

export const SYNC_POSITION_OUTCOMES = ['accepted', 'duplicate', 'rejected'] as const;

export const syncPositionResultSchema = z.object({
  key: z.string(),
  outcome: z.enum(SYNC_POSITION_OUTCOMES),
  /** Present only when `outcome` is `rejected`. */
  reason: z.enum(SYNC_POSITION_REJECTION_REASONS).optional(),
});
export type SyncPositionResult = z.infer<typeof syncPositionResultSchema>;

/** One result per submitted position, in the order sent. */
export const syncPositionsResponseSchema = z.object({ results: z.array(syncPositionResultSchema) });
export type SyncPositionsResponse = z.infer<typeof syncPositionsResponseSchema>;
